import { ILocalStorage } from '../../storage/interfaces';
import { IAuthService, ResponseError } from './interfaces';
import { StorageKeys } from '../../../utils/constants';
import { Session } from '../../user/model/interfaces';
import Firebase, {
  ApplicationVerifier,
  ConfirmationResult,
  UserCredential,
} from '../../../services/firebase/firebase';
import { ResponseUserAuthData } from '@base/features/auth/model/interfaces';
import { ISocketClient } from '@base/features/socket/interfaces';
import { IApiClient } from '@base/features/api/model/apiClient';
import API_PATHS from '@base/features/api/constants/apiPaths';
import { deserialize } from 'object-deserializer';
import {
  responseErrorDeserializer,
  responseUserAuthDataDeserializer,
} from '@base/features/user/model/deserializers';
import { BehaviorSubject } from 'rxjs';

class AuthService implements IAuthService {
  userSession: BehaviorSubject<Session | undefined>;
  constructor(
    private appId: string,
    private gameId: number,
    private apiClient: IApiClient,
    private socketClient: ISocketClient,
    private firebaseService: Firebase,
    private localStorage: ILocalStorage
  ) {
    const userAuthData: Session = this.localStorage.get(StorageKeys.SESSION);
    this.userSession = new BehaviorSubject<Session | undefined>(userAuthData);

    if (userAuthData) {
      try {
        this.socketClient.connect(userAuthData);
        this.socketClient.setDefaultConnection(userAuthData);
      } catch (e) {
        console.error(e);
        this.logout();
      }
    }
  }

  getUserSession(): Session {
    return this.localStorage.get(StorageKeys.SESSION);
  }

  async login(username: string, password: string) {
    const res = await this.apiClient.post(
      {
        usr: username,
        password: password,
        do: 'login',
        appId: this.appId,
        game_id: this.gameId,
      },
      API_PATHS.api
    );
    const data = await res.json();
    if (data.code === 0) {
      return deserialize(data, responseUserAuthDataDeserializer);
    } else {
      return deserialize(data, responseErrorDeserializer);
    }
  }

  async authorizationGuestPlayer(uuid: string, firebasetoken: string): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        uuid: uuid,
        firebasetoken: firebasetoken,
        do: 'login',
        appId: this.appId,
        game_id: this.gameId,
      },
      API_PATHS.api
    );

    const data = await res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  async authorizationApple(id_token: string): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post({ do: 'login', id_token }, API_PATHS.api);
    const data = await res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  async authorizationGoogle(
    googleToken: string,
    googleTokenId: string,
    email: string,
    name: string
  ): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'login',
        googletoken: googleToken,
        googletokenid: googleTokenId,
        usr: name,
        email: email,
        appId: this.appId,
        game_id: this.gameId,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  async checkUsernameAvailability(username: string) {
    const res = await this.apiClient.post(
      {
        name: username,
        do: 'is_name_available',
      },
      API_PATHS.api
    );
    const data = await res.json();
    return data;
  }

  async logout() {
    const res = await this.apiClient.post(
      {
        do: 'logout',
        appId: this.appId,
      },
      API_PATHS.api
    );
    const data = await res.json();
    this.userSession.next(undefined);
    this.socketClient.closeConnection();
    this.localStorage.delete(StorageKeys.SESSION);
    return data;
  }

  async registrationWithPhone(
    login: string,
    password: string,
    phone: string,
    recommender?: string
  ): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'reg',
        newusr: login,
        phone: phone,
        password: password,
        recommender: recommender ? recommender : '',
      },
      API_PATHS.api
    );
    const data = await res.json();
    return data;
  }

  async registrationWithEmail(
    login: string,
    password: string,
    email: string,
    recommender?: string
  ): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'reg',
        newusr: login,
        email: email,
        password: password,
        recommender: recommender ? recommender : '',
      },
      API_PATHS.api
    );
    const data = await res.json();
    return data;
    // return deserialize(data, responseUserAuthDataDeserializer);
  }

  async completeRegistration(
    firstName: string,
    lastName: string,
    yob: string,
    gender: string,
    country?: string,
    state?: string,
    zip?: string
  ): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.authPost(
      {
        do: 'vip_reg',
        first_name: firstName,
        last_name: lastName,
        yob: yob,
        gender: gender,
        country: country,
        state: state,
        zip: zip,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return data;
    // return deserialize(data, responseUserAuthDataDeserializer);
  }

  async setUserPhone(
    login: string,
    session: string,
    phone: string,
    token: string
  ): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'setphone',
        phone: phone,
        token: token,
        sid: session,
        usr: login,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  async setEmail(login: string, session: string, email: string): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'change_email',
        new_email: email,
        sid: session,
        usr: login,
      },
      API_PATHS.api
    );

    const data = await res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  async resetPassword(login: string, phone?: string, email?: string): Promise<any> {
    const params = {
      usr: login,
      email,
      phone,
      json: 1,
    };
    const response = await this.apiClient.get(params, API_PATHS.lostPassword);
    if (!response) throw new Error('missing response');
    return await response.json();
  }

  async changePassword(
    oldPassword: string,
    password: string,
    confPassword: string,
    session: string
  ): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'change_password',
        sid: session,
        newPassword: password,
        newPassword2: confPassword,
        oldPassword: oldPassword,
      },
      API_PATHS.api
    );
    const data = await res.json();
    return deserialize(data, responseUserAuthDataDeserializer);
  }

  getFacebookLink(): Promise<any> {
    return this.apiClient.post(
      {
        do: 'get_fb_link',
        game_id: this.gameId,
      },
      API_PATHS.api
    );
  }

  doLoginDefault(username: string, password: string): Promise<ResponseUserAuthData | ResponseError> {
    return this.login(username, password);
  }

  doLoginGuest(): Promise<firebase.auth.UserCredential> {
    return this.firebaseService.doSignInAnonymously();
  }

  async doLoginFacebook(facebookToken: string, email: string): Promise<ResponseUserAuthData> {
    const res = await this.apiClient.post(
      {
        do: 'login',
        fbtoken: facebookToken,
        email: email,
        appId: this.appId,
        game_id: this.gameId,
      },
      API_PATHS.api
    );
    return await res.json();
  }

  doLoginGoogle(
    googleToken: string,
    googleTokenId: string,
    email = '',
    name = ''
  ): Promise<ResponseUserAuthData> {
    return this.authorizationGoogle(googleToken, googleTokenId, email, name);
  }

  doLoginApple(idToken: string): Promise<ResponseUserAuthData> {
    return this.authorizationApple(idToken);
  }

  doEntryDefault(userData: ResponseUserAuthData): void {
    const { host, port, proxies: proxy, usr, session } = userData;

    if (userData.wssproxy2) proxy.unshift(userData.wssproxy2);
    if (userData.wssproxy) proxy.unshift(userData.wssproxy);

    const config = { proxy, host, port, usr, session };
    const successProxy = this.socketClient.connect(config);
    this.socketClient.setDefaultConnection({ ...config, proxy: successProxy });
    const newSession = {
      proxy: successProxy,
      host,
      port,
      usr,
      session,
    };
    this.localStorage.update(StorageKeys.SESSION, newSession);
    this.userSession.next(newSession);
  }

  checkIsVipRegistrationAccount(tries: number): boolean {
    return tries === -1;
  }

  requestSmsCode(phoneNumber: string, recaptchaVerifier: ApplicationVerifier): Promise<ConfirmationResult> {
    return this.firebaseService.doSignInWithPhoneNumber(phoneNumber, recaptchaVerifier);
  }

  signInWithCredential(verificationId: string, code: string): Promise<UserCredential> {
    return this.firebaseService.doSignInWithCredential(verificationId, code);
  }
}

export default AuthService;
